/**
 * Preset Relaxation Configurations
 * 
 * These are pre-defined configurations that hosts can choose from
 * Each represents a different philosophy on handling stuck matchmaking
 * 
 * Defaults (all 9 levels):
 * 1. Category gaps
 * 2. Cooldown
 * 3. Min+2 fairness guard
 * 4. Exact repeat games (+ same-quartet reshuffle)
 * 5. Session interaction density
 * 6. Back-to-back / consecutive 3rd interaction
 * 7. Consecutive 4th+ interaction
 * 8. Immediate repeat teams
 * 9. Game type restrictions
 */

import { DEFAULT_RELAXATION_CONFIG } from './normalizedRelaxation.js';

/**
 * Available preset configurations
 */
export const RELAXATION_PRESETS = {
  /**
   * Default/Balanced - Good for most sessions
   * Uses the standard relaxation order
   */
  default: DEFAULT_RELAXATION_CONFIG,
  
  /**
   * Strict - Minimal relaxation
   * Only allows skill gaps and one consecutive, stops early
   */
  strict: {
    enabled: true,
    maxLevel: 3,  // Skill gaps + consecutive-3 tier only (other steps disabled below)
    relaxationOrder: [
      { type: 'categoryGap', priority: 1 },
      { type: 'cooldown', priority: 2 },
      { type: 'minPlus2', priority: 3 },
      { type: 'exactRepeat', priority: 4 },
      { type: 'sessionInteractionDensity', priority: 5 },
      { type: 'consecutive3', priority: 6 },
      { type: 'consecutive4', priority: 7 },
      { type: 'immediateRepeat', priority: 8 },
      { type: 'gameType', priority: 9 },
    ],
    relaxations: {
      ...DEFAULT_RELAXATION_CONFIG.relaxations,
      consecutive4: { ...DEFAULT_RELAXATION_CONFIG.relaxations.consecutive4, enabled: false },
      exactRepeat: { ...DEFAULT_RELAXATION_CONFIG.relaxations.exactRepeat, enabled: false },
      minPlus2: { ...DEFAULT_RELAXATION_CONFIG.relaxations.minPlus2, enabled: false },
      cooldown: { ...DEFAULT_RELAXATION_CONFIG.relaxations.cooldown, enabled: false },
      gameType: { ...DEFAULT_RELAXATION_CONFIG.relaxations.gameType, enabled: false },
      sessionInteractionDensity: {
        ...DEFAULT_RELAXATION_CONFIG.relaxations.sessionInteractionDensity,
        enabled: false,
      },
    },
    description: 'Strict quality — widens tier gaps first, allows 3rd consecutive interactions, then repeat-pair rules (minimal relaxation)',
    bestFor: 'Competitive sessions, tournaments, high-quality matches required',
  },
  
  /**
   * Flexible - Relaxes aggressively to keep games flowing
   * Prioritizes variety over game type, goes to level 9
   */
  flexible: {
    enabled: true,
    maxLevel: 9,
    relaxationOrder: [
      { type: 'sessionInteractionDensity', priority: 1 },
      { type: 'consecutive3', priority: 2 },
      { type: 'exactRepeat', priority: 3 },
      { type: 'consecutive4', priority: 4 },
      { type: 'immediateRepeat', priority: 5 },
      { type: 'categoryGap', priority: 6 },
      { type: 'minPlus2', priority: 7 },
      { type: 'cooldown', priority: 8 },
      { type: 'gameType', priority: 9 },
    ],
    relaxations: DEFAULT_RELAXATION_CONFIG.relaxations,
    description: 'Flexible matching - prioritizes keeping games flowing (all 9 levels)',
    bestFor: 'Social sessions, beginners, sessions where game flow is critical',
  },
  
  /**
   * Variety-First - Prioritizes fresh matchups
   * Relaxes density and consecutives before anything else
   */
  varietyFirst: {
    enabled: true,
    maxLevel: 9,
    relaxationOrder: [
      { type: 'sessionInteractionDensity', priority: 1 },
      { type: 'consecutive3', priority: 2 },
      { type: 'exactRepeat', priority: 3 },
      { type: 'consecutive4', priority: 4 },
      { type: 'immediateRepeat', priority: 5 },
      { type: 'categoryGap', priority: 6 },
      { type: 'minPlus2', priority: 7 },
      { type: 'cooldown', priority: 8 },
      { type: 'gameType', priority: 9 },
    ],
    relaxations: DEFAULT_RELAXATION_CONFIG.relaxations,
    description: 'Variety-first - keeps matchups fresh at all costs',
    bestFor: 'Large clubs, networking events, emphasis on meeting new people',
  },
  
  /**
   * Game-Type-Protected - Never relaxes game type
   * Will accept fairness compromises before violating game type
   */
  gameTypeProtected: {
    enabled: true,
    maxLevel: 8,
    relaxationOrder: [
      { type: 'categoryGap', priority: 1 },
      { type: 'cooldown', priority: 2 },
      { type: 'minPlus2', priority: 3 },
      { type: 'exactRepeat', priority: 4 },
      { type: 'sessionInteractionDensity', priority: 5 },
      { type: 'consecutive3', priority: 6 },
      { type: 'consecutive4', priority: 7 },
      { type: 'immediateRepeat', priority: 8 },
      { type: 'gameType', priority: 9 },
    ],
    relaxations: {
      ...DEFAULT_RELAXATION_CONFIG.relaxations,
      gameType: { ...DEFAULT_RELAXATION_CONFIG.relaxations.gameType, enabled: false },
    },
    description: 'Game type protected - respects session configuration strictly',
    bestFor: 'Teaching sessions (e.g., mixed-only), specific strategy focus, club policies',
  },
  
  /**
   * Quality-Focused - Skill balance and fairness are most important
   * Will relax variety before skill gaps or fairness
   */
  qualityFocused: {
    enabled: true,
    maxLevel: 8,
    relaxationOrder: [
      { type: 'sessionInteractionDensity', priority: 1 },
      { type: 'consecutive3', priority: 2 },
      { type: 'exactRepeat', priority: 3 },
      { type: 'consecutive4', priority: 4 },
      { type: 'immediateRepeat', priority: 5 },
      { type: 'cooldown', priority: 6 },
      { type: 'categoryGap', priority: 7 },
      { type: 'minPlus2', priority: 8 },
      { type: 'gameType', priority: 9 },
    ],
    relaxations: DEFAULT_RELAXATION_CONFIG.relaxations,
    description: 'Quality-focused - maintains skill balance and fairness as long as possible',
    bestFor: 'Competitive sessions, skill-focused training, league play',
  },
  
  /**
   * Cooldown-Strict - Never relaxes cooldown
   * For clubs that strictly enforce waiting/observation requirements
   */
  cooldownStrict: {
    enabled: true,
    maxLevel: 6,
    relaxationOrder: [
      { type: 'categoryGap', priority: 1 },
      { type: 'cooldown', priority: 2 },
      { type: 'minPlus2', priority: 3 },
      { type: 'exactRepeat', priority: 4 },
      { type: 'sessionInteractionDensity', priority: 5 },
      { type: 'consecutive3', priority: 6 },
      { type: 'consecutive4', priority: 7 },
      { type: 'immediateRepeat', priority: 8 },
      { type: 'gameType', priority: 9 },
    ],
    relaxations: {
      ...DEFAULT_RELAXATION_CONFIG.relaxations,
      cooldown: { ...DEFAULT_RELAXATION_CONFIG.relaxations.cooldown, enabled: false },
      gameType: { ...DEFAULT_RELAXATION_CONFIG.relaxations.gameType, enabled: false },
    },
    description: 'Cooldown strict - never includes players who haven\'t observed enough',
    bestFor: 'Clubs with strict cooldown policies, beginners-focused sessions',
  },
  
  /**
   * No-Relaxation - Strict only, no progressive relaxation
   * If no match found at strict level, system gives up
   */
  noRelaxation: {
    enabled: false,
    maxLevel: 0,
    relaxationOrder: [],
    relaxations: {},
    description: 'No relaxation - strict quality or no game',
    bestFor: 'Very specific session requirements, manual override preferred',
  },
};

/**
 * Get preset configuration by key
 * @param {string} presetKey - Key from RELAXATION_PRESETS
 * @returns {Object} Configuration object
 */
export function getRelaxationPreset(presetKey) {
  if (!RELAXATION_PRESETS[presetKey]) {
    console.warn(`Unknown relaxation preset "${presetKey}", falling back to default`);
    return RELAXATION_PRESETS.default;
  }
  return RELAXATION_PRESETS[presetKey];
}

/**
 * Get list of available presets with metadata
 * Useful for building a UI selector
 */
export function getAvailableRelaxationPresets() {
  return Object.entries(RELAXATION_PRESETS).map(([key, config]) => ({
    key,
    name: key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1').trim(),
    description: config.description || 'Custom configuration',
    bestFor: config.bestFor || 'General use',
    maxLevel: config.maxLevel,
    enabled: config.enabled,
  }));
}
